'use client';

import { c as _c } from "react/compiler-runtime";
import { unwrapFromResult } from '@shared/utils/helpers/resultWrapper';
import { approvedSetQueryKey } from '@shared/utils/hooks/useApprovedSet';
import useAuth from '@shared/utils/hooks/useAuth';
import useUser from '@shared/utils/hooks/useUser';
import { clearApproved, saveApproved } from '@shared/utils/storage/approvedVerificationStorage';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { client, service } from '../../utils/trpc';
import { parseBackup } from '../../utils/verificationBackup';
import Button from '../common/Button';
import Confirmation from '../common/Confirmation';
import { VerificationRequestModal } from '../verification/VerificationRequestModal';
export const VerificationCard = () => {
  const $ = _c(40);
  const {
    currentUser
  } = useUser();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      client,
      service
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    initializeUser
  } = useAuth(t0);
  const queryClient = useQueryClient();
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const importFileRef = useRef(null);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      queryKey: ["verification.fees"],
      queryFn: _temp,
      staleTime: 3600000
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    data: fees
  } = useQuery(t1);
  const isVerified = currentUser.tag === "VERIFIED";
  const isPending = !!currentUser.verificationRequestedAt;
  let t2;
  if ($[2] !== currentUser.id || $[3] !== initializeUser || $[4] !== queryClient) {
    t2 = async () => {
      await client.verification.removeVerified.mutate();
      await clearApproved(currentUser.id);
      queryClient.invalidateQueries({
        queryKey: approvedSetQueryKey(currentUser.id)
      });
      await initializeUser();
      setShowRemoveConfirm(false);
    };
    $[2] = currentUser.id;
    $[3] = initializeUser;
    $[4] = queryClient;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const removeVerified = t2;
  let t3;
  if ($[6] !== currentUser.id || $[7] !== queryClient) {
    t3 = async e => {
      const file = e.target.files?.[0];
      e.target.value = "";
      if (!file) {
        return;
      }
      ;
      try {
        const set = await parseBackup(file, currentUser.id);
        await saveApproved(currentUser.id, set);
        queryClient.invalidateQueries({
          queryKey: approvedSetQueryKey(currentUser.id)
        });
        window.alert("Verification backup restored to this device.");
      } catch (t4) {
        const err = t4;
        window.alert(err instanceof Error ? err.message : "Import failed.");
      }
    };
    $[6] = currentUser.id;
    $[7] = queryClient;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const onImportFileChange = t3;
  const statusText = isVerified ? "\u2713 Verified by an admin" : isPending ? "Awaiting admin review" : "Not verified";
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <h3 className="text-xl font-semibold">Verified Account</h3>;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const t5 = `text-sm ${isVerified ? "text-emerald-500" : "text-textSecondary/60"}`;
  let t6;
  if ($[10] !== statusText || $[11] !== t5) {
    t6 = <div>{t4}<p className={t5}>{statusText}</p></div>;
    $[10] = statusText;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== fees) {
    t7 = fees && <>{" "}Costs {Number(fees.feeTrons).toLocaleString()} TRONs (${fees.feeDollars}), refunded if you cancel or are rejected.</>;
    $[13] = fees;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t7) {
    t8 = <p className="text-textSecondary/60">Live ID check by an admin. After approval, your bio and avatar can only be changed to admin-approved values.{t7}</p>;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== isPending || $[18] !== isVerified) {
    t9 = !isVerified && !isPending && <Button text="Request Verification" onClick={() => setShowRequestModal(true)} />;
    $[17] = isPending;
    $[18] = isVerified;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== isPending) {
    t10 = isPending && <Button text="Requested" variant="SECONDARY" onClick={() => setShowRequestModal(true)} />;
    $[20] = isPending;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== isVerified || $[23] !== onImportFileChange) {
    t11 = isVerified && <div className="flex flex-wrap items-center gap-2"><Button text="Add Bios/Avatars" variant="SECONDARY" onClick={() => setShowRequestModal(true)} /><Button text="Import Backup" variant="SECONDARY" icon={<Upload size={16} />} onClick={() => importFileRef.current?.click()} /><Button text="Remove Verification" variant="DANGER" onClick={() => setShowRemoveConfirm(true)} /><input ref={importFileRef} type="file" onChange={onImportFileChange} className="hidden" /></div>;
    $[22] = isVerified;
    $[23] = onImportFileChange;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t6 || $[28] !== t8 || $[29] !== t9) {
    t12 = <div className="space-y-4">{t6}{t8}{t9}{t10}{t11}</div>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t6;
    $[28] = t8;
    $[29] = t9;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  let t13;
  if ($[31] !== showRequestModal) {
    t13 = showRequestModal && <VerificationRequestModal closeHandler={() => setShowRequestModal(false)} />;
    $[31] = showRequestModal;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] !== removeVerified || $[34] !== showRemoveConfirm) {
    t14 = showRemoveConfirm && <Confirmation title="Remove Verification?" message="You'll lose your verified badge. Bio and avatar restrictions will lift; old approved hashes stay in storage but are unreachable." actionButtonText="Remove Verification" variant="DANGER" confirmHandler={removeVerified} cancelHandler={() => setShowRemoveConfirm(false)} />;
    $[33] = removeVerified;
    $[34] = showRemoveConfirm;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t12 || $[37] !== t13 || $[38] !== t14) {
    t15 = <>{t12}{t13}{t14}</>;
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
    $[39] = t15;
  } else {
    t15 = $[39];
  }
  return t15;
};
export default VerificationCard;
function _temp() {
  return unwrapFromResult(client.verification.getFees.query());
}